// SPDX-License-Identifier: SEE LICENSE
import type {
  SessionListItem,
  SessionRecord,
  SessionSummary,
  TelemetryFrame,
} from './lib/types';

// Same-origin in production (relative URLs), explicit override otherwise.
// Defaulting to '' means every fetch and EventSource hits whichever host
// served the SPA — exactly what the nginx vhost is set up to proxy.
export const BACKEND =
  (import.meta.env.VITE_PUBLIC_BACKEND_URL as string | undefined) ?? '';

// ── Dashboard auth ───────────────────────────────────────────────────────────
export interface AuthSession {
  authed: boolean;
  user?: string;
  enforced?: boolean;
}

export async function getAuthSession(): Promise<AuthSession> {
  try {
    const res = await fetch(`${BACKEND}/v1/auth/session`, {
      credentials: 'include',
      headers: { 'ngrok-skip-browser-warning': 'true' },
    });
    if (!res.ok) return { authed: false };
    return (await res.json()) as AuthSession;
  } catch {
    return { authed: false };
  }
}

export async function loginDashboard(username: string, password: string): Promise<string> {
  const res = await fetch(`${BACKEND}/v1/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      body.error ?? (res.status === 429 ? 'Too many attempts — wait and retry.' : 'Login failed'),
    );
  }
  const body = (await res.json()) as { user?: string };
  return body.user ?? username;
}

export async function logoutDashboard(): Promise<void> {
  await fetch(`${BACKEND}/v1/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  }).catch(() => undefined);
}

export async function listSessions(): Promise<{ sessions: SessionListItem[] }> {
  const res = await fetch(`${BACKEND}/v1/sessions`, {
    credentials: 'include',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export interface SessionSnapshot {
  session: SessionRecord;
  summary: SessionSummary;
  frames: Record<string, TelemetryFrame[]>;
}

export async function getSession(id: string): Promise<SessionSnapshot> {
  const res = await fetch(`${BACKEND}/v1/sessions/${id}`, {
    credentials: 'include',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export function sessionStreamUrl(id: string): string {
  return `${BACKEND}/v1/sessions/${id}/stream`;
}

export function sessionsStreamUrl(): string {
  return `${BACKEND}/v1/sessions/stream`;
}

export function certBundleUrl(short: string): string {
  return `${BACKEND}/v1/public/cert/session/${short}`;
}

// Public single-certificate verification — authorized by the per-cert
// capability token (`?t=`) carried in that cert's QR. Without a valid token the
// backend returns 401, so the cert is not openly public.
export async function getPublicCertByShort(short: string, token: string): Promise<unknown> {
  const q = token ? `?t=${encodeURIComponent(token)}` : '';
  const res = await fetch(`${BACKEND}/v1/public/cert/session/${short}/bundle${q}`, {
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });
  if (!res.ok) {
    throw new Error(
      res.status === 404
        ? 'Certificate not found'
        : res.status === 401
          ? 'This verification link is invalid or has expired.'
          : await res.text(),
    );
  }
  return res.json();
}

export async function getSessionCertificate(id: string): Promise<unknown> {
  const res = await fetch(`${BACKEND}/v1/session/${id}/certificate`, {
    credentials: 'include',
    headers: { 'ngrok-skip-browser-warning': 'true' },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function mintSessionCertificate(id: string): Promise<unknown> {
  const res = await fetch(`${BACKEND}/v1/session/${id}/certificate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
    body: '{}',
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
